import _ from 'lodash'
import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, Method, RawAxiosRequestHeaders } from 'axios'
import qs from 'qs'

import { ErrorResponse } from '@/core/types/response'
import { getEnv } from '@/utils/helpers'
import { NotFoundError, UnknownError } from './errors'
import keycloak from '../../keycloak'
import { ensureFreshKeycloakToken, refreshKeycloakToken } from '@/utils/auth-token'

const baseURL = getEnv('VITE_API_URL')!

export const axios = Axios.create({ baseURL })

interface AuthRefreshAxiosRequestConfig extends AxiosRequestConfig {
  _retryAuthRefresh?: boolean
}

function isExternalUrl(url?: string): boolean {
  return !!url && /^https?:\/\//i.test(url) && !url.startsWith(baseURL)
}

function setAuthorizationHeader(config: AxiosRequestConfig, token: string): void {
  config.headers = {
    ...((config.headers ?? {}) as RawAxiosRequestHeaders),
    Authorization: `Bearer ${token}`,
  }
}

axios.interceptors.request.use(async (config) => {
  if (isExternalUrl(config.url)) {
    return config
  }

  const token = await ensureFreshKeycloakToken()
  if (token) {
    setAuthorizationHeader(config, token)
  }

  return config
})

axios.interceptors.response.use(undefined, async (error: unknown) => {
  if (!Axios.isAxiosError(error) || error.response?.status !== 401 || !error.config || isExternalUrl(error.config.url)) {
    return Promise.reject(error)
  }

  const config = error.config as AuthRefreshAxiosRequestConfig
  if (config._retryAuthRefresh) {
    return Promise.reject(error)
  }

  config._retryAuthRefresh = true

  try {
    const token = await refreshKeycloakToken()
    if (!token) {
      return Promise.reject(error)
    }

    setAuthorizationHeader(config, token)
    return axios(config)
  } catch {
    return Promise.reject(error)
  }
})

export class HttpClient {
  axios: AxiosInstance = axios
  private isInSyncOperation = false

  setSyncMode(enabled: boolean) {
    this.isInSyncOperation = enabled
  }

  async request<T>(route: string, method: Method, args?: any, token?: string): Promise<T> {
    const requestConfig: AxiosRequestConfig = {
      url: route,
      method,
      headers: {
        'Content-Type': 'application/json',
        // TODO : don't allow this in production
        ...(getEnv('NODE_ENV') !== 'prod' && { 'ngrok-skip-browser-warning': '69420' }),
      },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'brackets' })
      },
    }
    if (_.includes(['POST', 'PUT', 'PATCH', 'DELETE'], method.toUpperCase())) {
      requestConfig.data = args
    } else {
      requestConfig.params = args
    }
    if (token) requestConfig.headers!.Authorization = `Bearer ${token}`
    try {
      const response = await axios(requestConfig) as AxiosResponse<{ datas: T }>
      return response.data.datas
    } catch (err) {
      if (Axios.isAxiosError(err)) {
        if (err.response) {
          const { status, data: errorData } = err.response as AxiosResponse<ErrorResponse>
          if (status === 401) {
            if (this.isInSyncOperation) {
              // During sync operations, throw the error instead of auto-logout
              // This allows the sync service to handle authentication gracefully
              throw new Error('Authentication expired during sync operation')
            } else {
              // if back says user is not authenticated redirect to /login
              keycloak.logout()
            }
          } else {
            switch (errorData.name) {
              case 'NotFoundException':
                throw new NotFoundError(errorData.messages.join('\n'), errorData.statusCode)
              default:
                throw new UnknownError(errorData.messages.join('\n'), errorData.statusCode)
            }
          }
        }
      }
      throw err
    }
  }
}

// Create a sync-aware HTTP client instance
export class SyncHttpClient extends HttpClient {
  constructor() {
    super()
    // Always enable sync mode for this client
    this.setSyncMode(true)
  }
}

// Export singleton instance for sync operations
export const syncHttpClient = new SyncHttpClient()
